import axios from 'axios';
import { getAuthHeaders } from '../Auth';

export interface ProductConfigurationResponse {
    _link?: {
        self?: {
            href: string;
        };
    };
    _embedded?: {
        configuration?: {
            uri?: string;
            productId?: string;
            version?: string;
            selections?: Record<string, string>;
        };
    };
    additionalData?: {
        merchantProduct?: {
            completedSelections?: Record<string, string>;
            rootProduct?: {
                productId?: string;
                version?: string;
                selections?: Record<string, string>;
            };
        };
    };
}

export interface GetProductConfigurationInput {
    productId: string;
    version?: string | number;
    selections?: Record<string, string> | string;
    signal?: AbortSignal;
}

export interface ParsedSurfaceUrlInfo {
    productId?: string;
    version?: string;
    selections?: Record<string, string>;
}

export function parseProductInfoFromSurfaceUrl(url: string): ParsedSurfaceUrlInfo {
    if (!url) return {};

    let productId: string | undefined;
    let version: string | undefined;
    const selections: Record<string, string> = {};

    try {
        const urlObj = new URL(url);
        const pathSegments = urlObj.pathname.split('/').filter(Boolean);

        const prdIndex = pathSegments.findIndex((seg) => /(PRD|CIM)-[A-Za-z0-9-]+/i.test(seg));
        if (prdIndex !== -1) {
            const prdMatch = pathSegments[prdIndex].match(/(PRD|CIM)-[A-Za-z0-9-]+/i);
            productId = prdMatch ? prdMatch[0] : pathSegments[prdIndex];

            const nextSegment = pathSegments[prdIndex + 1];
            if (nextSegment) {
                const versionMatch = nextSegment.match(/^(\d+)/);
                if (versionMatch) {
                    version = versionMatch[1];
                }
            }
        }

        urlObj.searchParams.forEach((value, key) => {
            if (key.startsWith('optionSelections[') && key.endsWith(']')) {
                const optKey = key.slice(17, -1);
                selections[optKey] = value;
            } else if (key === 'optionSelections' || key === 'variables' || key === 'selections') {
                try {
                    const parsed = JSON.parse(value);
                    if (typeof parsed === 'object' && parsed !== null) {
                        Object.assign(selections, parsed);
                    }
                } catch {
                    // ignore JSON parse error
                }
            }
        });
    } catch {
        const match = url.match(/(PRD|CIM)-[A-Za-z0-9-]+/i);
        if (match) {
            productId = match[0];
            const versionMatch = url.match(new RegExp(`${productId}/(\\d+)`));
            if (versionMatch) {
                version = versionMatch[1];
            }
        }
    }

    return {
        productId,
        version: version || '1',
        selections: Object.keys(selections).length > 0 ? selections : undefined,
    };
}

export async function getProductConfiguration(
    input: GetProductConfigurationInput,
): Promise<ProductConfigurationResponse | null> {
    const { productId, version = '1', selections, signal } = input;

    if (!productId || productId.trim() === '') {
        return null;
    }

    const versionStr = String(version || '1');
    const baseUrl = `https://cdn-merchant-data-orchestration.products.cimpress.io/v1/products/${encodeURIComponent(productId)}/versions/${encodeURIComponent(versionStr)}:productconfiguration`;

    let fullUrl = baseUrl;
    if (selections) {
        let selectionsStr = typeof selections === 'object' ? JSON.stringify(selections) : selections;
        try {
            if (typeof selections === 'string' && selections.includes('%')) {
                selectionsStr = decodeURIComponent(selections);
            }
        } catch {
            // ignore
        }
        fullUrl = `${baseUrl}?selections=${encodeURIComponent(selectionsStr)}`;
    }

    try {
        const headers = await getAuthHeaders();
        const response = await axios.get<ProductConfigurationResponse>(fullUrl, {
            headers,
            signal,
        });

        return response.data;
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }

        console.error(`Failed to fetch product configuration for PRD ${productId} version ${versionStr}:`, error);
        return null;
    }
}

export async function convertPrdToCimSku(
    inputOrUrlOrProductId: string | GetProductConfigurationInput,
    signal?: AbortSignal,
): Promise<string | null> {
    let input: GetProductConfigurationInput;

    if (typeof inputOrUrlOrProductId === 'string') {
        if (inputOrUrlOrProductId.includes('://')) {
            const parsed = parseProductInfoFromSurfaceUrl(inputOrUrlOrProductId);
            if (!parsed.productId) {
                return null;
            }
            input = {
                productId: parsed.productId,
                version: parsed.version,
                selections: parsed.selections,
                signal,
            };
        } else {
            input = { productId: inputOrUrlOrProductId, signal };
        }
    } else {
        input = inputOrUrlOrProductId;
    }

    const config = await getProductConfiguration(input);
    return (
        config?._embedded?.configuration?.productId ||
        config?.additionalData?.merchantProduct?.rootProduct?.productId ||
        null
    );
}
